import { incrementSessionStat } from 'src/game/stats/stats.modifiers';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  getAvailablePaymentMethods,
  getAvailableProviders,
  getWebPaySequenceStats,
  methodToProviderMapping,
} from '../iap.utils';
import { webIAPPrices, type ProductId } from 'src/replicant/iap/iap.ruleset';
import { PaymentPopupResult, PaymentMethodResult } from '../iap.types';
import { WebPaymentMethodAnalytics } from '../analytics/analytics.types';
import { getSession } from 'src/game/stats/stats.getters';
import { getPersistent } from 'src/replicant/stats/stats.getters';
import { trackWebPayOpen } from '../analytics/iap.web.pay';

/**
 * Tracks the event of opening a web payment popup for a specific product.
 *
 * This function increments both persistent and session statistics for the web payment sequence,
 * and then logs the event with relevant details such as available providers, provider methods,
 * product ID, product price, and additional sequence statistics.
 *
 * @param {ProductId} productId - The ID of the product for which the web payment popup is opened.
 *
 * @returns {Promise<void>} A promise that resolves when the tracking is complete.
 */
export async function trackPopupOpenWebPayShow(productId: ProductId) {
  await StateObserver.invoke.incrementPersistentStat('webPaySequence');
  incrementSessionStat('webPaySequence');

  trackWebPayOpen({
    availableProviders: getAvailableProviders(),
    availableProviderMethods: getAvailablePaymentMethods().list,
    productID: productId,
    productPrice: webIAPPrices[productId],
    ...getWebPaySequenceStats(),
  });
}

/**
 * Creates an analytics object for the PopupOpenWebPay close result
 *
 * @param methodResult - The result of the payment method.
 * @param opts - The options for creating the analytics object.
 * @param opts.productId - The ID of the product.
 * @param opts.openTimestamp - The timestamp when the popup was opened.
 *
 * @returns The result of the payment popup with compiled analytics.
 */
export function createPaymentPopupResult(
  methodResult: PaymentMethodResult,
  opts: {
    productId: ProductId;
    openTimestamp: number;
  },
): PaymentPopupResult {
  return {
    ...methodResult.paymentResult,
    ...createPaymentPopupAnalytics(methodResult, opts),
    methodTimestamp: methodResult.methodData?.methodTimestamp,
  };
}

/**
 * Creates analytics data for a payment popup.
 *
 * @param opts - The options for creating the payment popup analytics.
 * @param opts.productId - The ID of the product.
 * @param opts.methodResult - The result of the payment method analytics.
 * @param opts.openTimestamp - The timestamp when the popup was opened.
 *
 * @returns An object containing the analytics data and method analytics.
 */
export function createPaymentPopupAnalytics(
  methodResult: WebPaymentMethodAnalytics,
  opts: {
    productId: ProductId;
    openTimestamp: number;
  },
) {
  const { productId, openTimestamp } = opts;

  // Get the common analytics data
  const analytics: PaymentPopupResult['analytics'] = {
    productID: productId,
    productPrice: webIAPPrices[productId],
    availableProviders: getAvailableProviders(),
    availableProviderMethods: getAvailablePaymentMethods().list,
    elapsedSecondsOpen: (now() - openTimestamp) / 1000,
    ...methodResult.analyticsData,
    ...getWebPaySequenceStats(),
  };

  // Get the method analytics if one is selected
  const methodData = methodResult.methodData;
  const methodAnalytics = compileMethodAnalytics(methodData);

  return {
    analytics,
    methodAnalytics,
  };
}

/**
 * Compiles analytics data for a given payment method.
 *
 * @param methodData - Optional data related to the payment method.
 * @returns An object containing analytics data for the payment method, or undefined if no method data is provided.
 */
function compileMethodAnalytics(
  methodData?: WebPaymentMethodAnalytics['methodData'],
): PaymentPopupResult['methodAnalytics'] | undefined {
  if (!methodData) {
    return undefined;
  }

  const method = methodData.providerMethod;
  const provider = methodToProviderMapping()[method];
  const availableMethods = getAvailablePaymentMethods().list;

  return {
    purchaseProvider: provider,
    purchaseProviderMethod: method,
    isMethodPromoted: availableMethods[0] === method,
    rankInMethodList: availableMethods.indexOf(method) + 1,
    elapsedSecondsSelect: (now() - methodData.methodTimestamp) / 1000,
    instanceSelectSequenceForMethod: methodData.instanceSelectSequenceForMethod,
    sessionSuccessSequenceForMethod: getSession(`${method}_sequence`, 0),
    allTimeSuccessSequenceForMethod: getPersistent(
      user(),
      `${method}_sequence`,
      0,
    ),
    hasAgeCheckbox: methodData.hasAgeCheckbox,
    purchaseID: 'unknown',
    internalTransactionId: 'unknown',
  };
}
